import { forwardRef, useEffect, useRef } from 'react';
import { handleRefSplit } from '@optimus-web/core';
import { removeBackendComponent } from './backendComponentsStore';
import { COMPONENT_TYPE, ComponentType, REACT_COMPONENT_DATA } from './constants';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    html: string | Blob;
    onRender?: (rootsCount: object) => void;
    onUnmount?: () => void;
    scrollRestoration?: { start: () => void; stop: () => void };
}

const reactComponentDataSelector = `[${REACT_COMPONENT_DATA}]`;
const componentDomSelector = [reactComponentDataSelector].join(', ');

export const DangerouslySetHtmlContent = forwardRef(
    ({ html, onRender, onUnmount, scrollRestoration, ...other }: Props, ref) => {
        const divRef = useRef<HTMLDivElement>(null);
        useEffect(() => {
            const { current } = divRef;
            if (!html || !current) {
                return;
            }

            let htmlContent: HTMLElement | DocumentFragment;
            if (html instanceof Blob) {
                htmlContent = document.createElement('embed');
                htmlContent.setAttribute('src', URL.createObjectURL(html));
                htmlContent.style.height = '100%';
            } else {
                // html comes from the server response
                // eslint-disable-next-line no-unsanitized/method
                htmlContent = document.createRange().createContextualFragment(html);
            }

            current.replaceChildren(htmlContent);

            window.setTimeout(() => {
                onRender?.({
                    reactLightweightRoots: current.querySelectorAll(`[${COMPONENT_TYPE}=${ComponentType.Lightweight}]`)
                        .length,
                    reactStandardRoots: current.querySelectorAll(`[${COMPONENT_TYPE}=${ComponentType.Standard}]`)
                        .length,
                });
                scrollRestoration?.start();
            }, 0);

            return () => {
                onUnmount?.();
                scrollRestoration?.stop();
                current.querySelectorAll(componentDomSelector).forEach((element) => {
                    if (!element) {
                        return;
                    }

                    if (element.matches(reactComponentDataSelector)) {
                        removeBackendComponent(element.id);
                    }
                });
            };
        }, [html, onRender, onUnmount, scrollRestoration]);

        return <div {...other} ref={handleRefSplit({ ownRef: divRef, forwardedRef: ref })} />;
    }
);
